import { useState, useEffect, useCallback } from 'react';
import { Button } from '../ui/button';
import { Card, CardContent } from '../ui/card';
import { toast } from 'sonner';
import {
  Camera, Upload, Trash2, Check, Loader2, ImagePlus, Share2
} from 'lucide-react';
import api from '../../services/api';
import { JournalShareDialog } from '../JournalShareDialog';
import { PhotoLightbox, DeleteConfirmDialog } from './PhotoLightbox';
import { isNative, nativeCamera } from '../../utils/capacitor';

const PhotoJournalTab = ({ itineraryId, days = [], destinationCity = '', destinationCountry = '', startDate = '' }) => {
  const [photos, setPhotos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [selectedDay, setSelectedDay] = useState(1);
  const [lightbox, setLightbox] = useState(null);
  const [editingCaption, setEditingCaption] = useState(null);
  const [captionText, setCaptionText] = useState('');
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [shareMode, setShareMode] = useState(false);
  const [shareSelected, setShareSelected] = useState(new Set());
  const [sharing, setSharing] = useState(false);
  const [shareDialog, setShareDialog] = useState(false);
  const [shareData, setShareData] = useState(null);
  const API = process.env.REACT_APP_BACKEND_URL;

  const fetchPhotos = useCallback(async () => {
    try {
      const res = await api.get(`/journal/${itineraryId}/photos`);
      if (res.data.success) setPhotos(res.data.photos);
    } catch {
      // silent
    } finally {
      setLoading(false);
    }
  }, [itineraryId]);

  useEffect(() => { fetchPhotos(); }, [fetchPhotos]);

  const handleUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;

    setUploading(true);
    let uploaded = 0;

    for (const file of files) {
      if (file.size > 5 * 1024 * 1024) {
        toast.error(`${file.name} exceeds 5MB limit`);
        continue;
      }
      const formData = new FormData();
      formData.append('file', file);
      formData.append('day_number', String(selectedDay));
      formData.append('caption', '');
      try {
        const res = await api.post(`/journal/${itineraryId}/photos`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        if (res.data.success) uploaded++;
      } catch {
        toast.error(`Failed to upload ${file.name}`);
      }
    }

    if (uploaded > 0) {
      toast.success(`${uploaded} photo${uploaded > 1 ? 's' : ''} uploaded!`);
      fetchPhotos();
    }
    setUploading(false);
    e.target.value = '';
  };

  const handleNativeCamera = async () => {
    try {
      const dataUrl = await nativeCamera.takePhoto();
      if (!dataUrl) return;

      setUploading(true);
      // Convert data URL to blob for upload
      const res = await fetch(dataUrl);
      const blob = await res.blob();
      const file = new File([blob], `photo_${Date.now()}.jpg`, { type: 'image/jpeg' });

      const formData = new FormData();
      formData.append('file', file);
      formData.append('day_number', String(selectedDay));
      formData.append('caption', '');

      const uploadRes = await api.post(`/journal/${itineraryId}/photos`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      if (uploadRes.data.success) {
        toast.success('Photo captured and uploaded!');
        fetchPhotos();
      }
    } catch (err) {
      if (err.message?.includes('cancelled') || err.message?.includes('User cancelled')) return;
      toast.error('Failed to capture photo');
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = (photoId) => {
    setDeleteConfirm(photoId);
  };

  const confirmDelete = async () => {
    if (!deleteConfirm) return;
    try {
      await api.delete(`/journal/${itineraryId}/photos/${deleteConfirm}`);
      setPhotos(prev => prev.filter(p => p.id !== deleteConfirm));
      if (lightbox?.photo?.id === deleteConfirm) setLightbox(null);
      toast.success('Photo deleted');
    } catch {
      toast.error('Failed to delete');
    } finally {
      setDeleteConfirm(null);
    }
  };

  const handleSaveCaption = async (photoId) => {
    const formData = new FormData();
    formData.append('caption', captionText);
    try {
      await api.put(`/journal/${itineraryId}/photos/${photoId}`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setPhotos(prev => prev.map(p => p.id === photoId ? { ...p, caption: captionText } : p));
      setEditingCaption(null);
      toast.success('Caption saved');
    } catch {
      toast.error('Failed to save caption');
    }
  };

  const photosByDay = {};
  photos.forEach(p => {
    if (!photosByDay[p.day_number]) photosByDay[p.day_number] = [];
    photosByDay[p.day_number].push(p);
  });

  const allPhotosFlat = photos;
  const openLightbox = (photo) => {
    if (shareMode) {
      toggleShareSelect(photo.id);
      return;
    }
    const idx = allPhotosFlat.findIndex(p => p.id === photo.id);
    setLightbox({ photo, index: idx });
  };

  const toggleShareSelect = (id) => {
    setShareSelected(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const handleShareSelected = async () => {
    if (shareSelected.size === 0) return;
    setSharing(true);
    try {
      const res = await api.post('/journal/share', {
        itinerary_id: itineraryId,
        photo_ids: [...shareSelected],
      });
      if (res.data.success) {
        const shareUrl = `${window.location.origin}/shared/journal/${res.data.share_id}`;
        const firstPhoto = photos.find(p => shareSelected.has(p.id));
        setShareData({
          photoUrl: firstPhoto ? getPhotoUrl(firstPhoto) : null,
          galleryUrl: shareUrl,
          photoCount: shareSelected.size,
        });
        setShareDialog(true);
      }
    } catch {
      toast.error('Failed to create share link');
    } finally {
      setSharing(false);
      setShareMode(false);
      setShareSelected(new Set());
    }
  };

  const handleShareAll = async () => {
    setSharing(true);
    try {
      const res = await api.post('/journal/share', {
        itinerary_id: itineraryId,
        photo_ids: photos.map(p => p.id),
      });
      if (res.data.success) {
        const shareUrl = `${window.location.origin}/shared/journal/${res.data.share_id}`;
        setShareData({
          photoUrl: photos[0] ? getPhotoUrl(photos[0]) : null,
          galleryUrl: shareUrl,
          photoCount: photos.length,
        });
        setShareDialog(true);
      }
    } catch {
      toast.error('Failed to create share link');
    } finally { setSharing(false); }
  };

  const handleShareSingle = async (photo) => {
    try {
      const res = await api.post('/journal/share', { itinerary_id: itineraryId, photo_ids: [photo.id] });
      if (res.data.success) {
        const shareUrl = `${window.location.origin}/shared/journal/${res.data.share_id}`;
        setShareData({
          photoUrl: getPhotoUrl(photo),
          galleryUrl: shareUrl,
          photoCount: 1,
        });
        setShareDialog(true);
        setLightbox(null);
      }
    } catch { toast.error('Failed to share'); }
  };
  const navLightbox = (dir) => {
    if (!lightbox) return;
    const newIdx = (lightbox.index + dir + allPhotosFlat.length) % allPhotosFlat.length;
    setLightbox({ photo: allPhotosFlat[newIdx], index: newIdx });
  };

  const getPhotoUrl = (photo) => `${API}/api/journal/file/${photo.storage_path}`;

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-8 h-8 animate-spin text-ocean" />
      </div>
    );
  }

  return (
    <div className="bg-white/95 backdrop-blur-xl border border-white/30 rounded-2xl p-6 shadow-lg space-y-6" data-testid="photo-journal-tab">
      {/* Header + Upload */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
        <div>
          <h3 className="font-serif text-xl text-ocean font-bold flex items-center gap-2">
            <Camera className="w-5 h-5 text-gold" /> Photo Journal
          </h3>
          <p className="text-charcoal/50 text-sm mt-1">{photos.length} photo{photos.length !== 1 ? 's' : ''} uploaded</p>
        </div>
        <div className="flex items-center gap-2">
          <select
            value={selectedDay}
            onChange={e => setSelectedDay(Number(e.target.value))}
            className="bg-ocean/10 border border-ocean/20 rounded-lg px-3 py-2 text-sm text-charcoal"
            data-testid="day-selector"
          >
            {(days.length > 0 ? days : [{ day_number: 1 }]).map((d) => (
              <option key={`day-opt-${d.day_number || 1}`} value={d.day_number || 1} className="text-ocean">
                Day {d.day_number || 1}{d.title ? `: ${d.title.slice(0, 30)}` : ''}
              </option>
            ))}
          </select>
          <label className="cursor-pointer" data-testid="upload-photos-btn">
            <input type="file" multiple accept="image/jpeg,image/png,image/webp" className="hidden" onChange={handleUpload} disabled={uploading} />
            <div className="flex items-center gap-1.5 bg-gold hover:bg-gold-dark text-ocean font-semibold text-sm px-4 py-2 rounded-lg transition-colors">
              {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
              {uploading ? 'Uploading...' : 'Upload'}
            </div>
          </label>
          {isNative && (
            <Button
              onClick={handleNativeCamera}
              disabled={uploading}
              className="bg-ocean hover:bg-ocean-light text-white font-semibold text-sm h-9 px-4 rounded-lg gap-1.5"
              data-testid="native-camera-btn"
            >
              <Camera className="w-4 h-4" /> Camera
            </Button>
          )}
          {photos.length > 0 && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => { setShareMode(!shareMode); setShareSelected(new Set()); }}
              className={`text-sm h-9 px-3 rounded-lg gap-1.5 ${shareMode ? 'bg-ocean text-white border-ocean' : 'border-ocean/20 text-ocean hover:bg-ocean/5'}`}
              data-testid="share-mode-btn"
            >
              <Share2 className="w-4 h-4" /> {shareMode ? 'Cancel' : 'Share'}
            </Button>
          )}
        </div>
      </div>

      {/* Share selection bar */}
      {shareMode && (
        <div className="flex items-center justify-between bg-ocean/5 border border-ocean/15 rounded-xl px-4 py-3">
          <p className="text-ocean text-sm font-medium">
            {shareSelected.size === 0 ? 'Tap photos to select' : `${shareSelected.size} photo${shareSelected.size > 1 ? 's' : ''} selected`}
          </p>
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="sm" onClick={() => setShareSelected(new Set(photos.map(p => p.id)))}
              className="text-ocean text-xs h-8 px-3" data-testid="select-all-share-btn">
              Select All
            </Button>
            <Button size="sm" onClick={handleShareSelected} disabled={shareSelected.size === 0 || sharing}
              className="bg-ocean hover:bg-ocean-dark text-white text-xs h-8 px-4 rounded-lg gap-1.5" data-testid="share-selected-btn">
              {sharing ? <Loader2 className="w-3 h-3 animate-spin" /> : <Share2 className="w-3 h-3" />}
              Share Card
            </Button>
          </div>
        </div>
      )}

      {/* Timeline by day */}
      {photos.length === 0 ? (
        <Card className="bg-ocean/5 border-ocean/10">
          <CardContent className="py-16 text-center">
            <ImagePlus className="w-12 h-12 text-ocean/20 mx-auto mb-3" />
            <p className="text-charcoal/50 text-sm">No photos yet. Upload your travel memories!</p>
            <p className="text-charcoal/30 text-xs mt-1">Select a day above and click Upload to add photos</p>
          </CardContent>
        </Card>
      ) : (
        (days.length > 0 ? days : [{ day_number: 1, title: 'Day 1' }]).map((day, di) => {
          const dayNum = day.day_number || di + 1;
          const dayPhotos = photosByDay[dayNum];
          if (!dayPhotos || dayPhotos.length === 0) return null;

          return (
            <div key={dayNum} className="space-y-3" data-testid={`journal-day-${dayNum}`}>
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-full bg-gold/20 flex items-center justify-center text-gold font-bold text-sm flex-shrink-0">
                  {dayNum}
                </div>
                <div>
                  <h4 className="text-ocean font-semibold text-sm">
                    Day {dayNum}{day.title ? `: ${day.title}` : ''}
                  </h4>
                  <p className="text-charcoal/40 text-xs">{dayPhotos.length} photo{dayPhotos.length > 1 ? 's' : ''}</p>
                </div>
              </div>

              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
                {dayPhotos.map(photo => (
                  <div
                    key={photo.id}
                    className={`relative group rounded-xl overflow-hidden cursor-pointer aspect-square transition-all ${shareMode && shareSelected.has(photo.id) ? 'border-2 border-ocean ring-2 ring-ocean/30' : 'border border-ocean/10'} bg-ocean/5`}
                    onClick={() => openLightbox(photo)}
                    data-testid={`journal-photo-${photo.id}`}
                  >
                    <img
                      src={getPhotoUrl(photo)}
                      alt={photo.caption || 'Travel photo'}
                      className="w-full h-full object-cover"
                      loading="lazy"
                    />
                    {/* Caption overlay */}
                    {photo.caption && (
                      <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent p-2">
                        <p className="text-white text-xs truncate">{photo.caption}</p>
                      </div>
                    )}
                    {/* Share selection indicator */}
                    {shareMode && (
                      <div className="absolute top-2 left-2">
                        <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center ${shareSelected.has(photo.id) ? 'bg-ocean border-ocean text-white' : 'border-white/80 bg-black/20'}`}>
                          {shareSelected.has(photo.id) && <Check className="w-3.5 h-3.5" />}
                        </div>
                      </div>
                    )}
                    {/* Delete button — hidden in share mode */}
                    {!shareMode && (
                      <button
                        onClick={e => { e.stopPropagation(); handleDelete(photo.id); }}
                        className="absolute top-2 right-2 w-7 h-7 rounded-full bg-red-500/90 hover:bg-red-600 text-white flex items-center justify-center shadow-md"
                        data-testid={`delete-photo-${photo.id}`}
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    )}
                  </div>
                ))}
              </div>
            </div>
          );
        })
      )}

      <PhotoLightbox
        lightbox={lightbox}
        allPhotosFlat={allPhotosFlat}
        editingCaption={editingCaption}
        captionText={captionText}
        setCaptionText={setCaptionText}
        setEditingCaption={setEditingCaption}
        onSaveCaption={handleSaveCaption}
        onClose={() => setLightbox(null)}
        onNav={navLightbox}
        onDelete={handleDelete}
        onShare={handleShareSingle}
        getPhotoUrl={getPhotoUrl}
      />

      <DeleteConfirmDialog
        show={!!deleteConfirm}
        onCancel={() => setDeleteConfirm(null)}
        onConfirm={confirmDelete}
      />

      {/* Journal Share Dialog */}
      <JournalShareDialog
        open={shareDialog}
        onOpenChange={setShareDialog}
        photoUrl={shareData?.photoUrl}
        destination={destinationCity}
        country={destinationCountry}
        date={startDate}
        photoCount={shareData?.photoCount || 0}
        galleryUrl={shareData?.galleryUrl}
      />
    </div>
  );
};

export default PhotoJournalTab;
